// One in-page question before something is thrown away.
//
// ⚠ WHY THIS EXISTS. Receive used `window.confirm` three times (discard a photo,
// discard a refused inspection, leave a draft). On a docked iPad kiosk the native
// dialog is unstyled, prints the hostname, and blocks the page - the queue's
// timers included - until it is answered. This is the operator shell's own
// version: the question is the title, one sentence says what is lost, and the
// destructive verb is the first, filled button. Escape keeps. Focus moves to the
// panel on open and returns to whatever opened it on close; everything else on
// the page is `inert` while it is up.
//
// Mount it only while a question is open - mounting is opening - and it is NOT
// shadcn (see CLAUDE.md "What it is": 56px gloved targets, one radius).
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { opFocus } from '../../shells/OperatorShell';

export interface ConfirmRequest {
  /** The question, as the title: "Discard this photo?" */
  title: string;
  /** One sentence of consequence: "The inspection is kept; only the photo goes." */
  consequence: string;
  /** The destructive verb: "Discard photo", "Leave". */
  confirmLabel: string;
  /** The way out: "Keep", "Stay". */
  keepLabel: string;
  onConfirm: () => void;
}

interface ConfirmPanelProps {
  request: ConfirmRequest;
  /** Called after either answer; the owner unmounts the panel. */
  onClose: () => void;
}

const BTN = `min-h-[56px] flex-1 rounded-(--radius) border border-(--border-strong) px-(--s4) font-semibold ${opFocus}`;

export function ConfirmPanel({ request, onClose }: ConfirmPanelProps) {
  const panel = useRef<HTMLElement>(null);
  const host = useRef<HTMLDivElement | null>(null);
  if (host.current === null && typeof document !== 'undefined') {
    host.current = document.createElement('div');
  }

  // Focus in on open, back to the opener on close; the rest of the page inert
  // meanwhile. One effect so the three undo in the right order.
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.appendChild(el);
    const made: Element[] = [];
    for (const child of Array.from(document.body.children)) {
      if (child === el || child.hasAttribute('inert')) continue;
      child.setAttribute('inert', '');
      made.push(child);
    }
    panel.current?.focus();
    return () => {
      for (const c of made) c.removeAttribute('inert');
      el.remove();
      opener?.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!host.current) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-(--s4) sm:items-center">
      <section
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-panel-title"
        aria-describedby="confirm-panel-consequence"
        tabIndex={-1}
        className={`w-full max-w-[560px] rounded-(--radius) border-2 border-(--warn-line) bg-(--warn-bg) p-(--s4) text-(--fg) ${opFocus}`}
      >
        <h2 id="confirm-panel-title" className="m-0 text-(length:--fs-lg) font-bold">
          {request.title}
        </h2>
        <p id="confirm-panel-consequence" className="m-0 mt-(--s2) text-(length:--fs-md)">
          {request.consequence}
        </p>
        <div className="mt-(--s3) flex gap-(--s3)">
          <button
            type="button"
            onClick={() => {
              request.onConfirm();
              onClose();
            }}
            className={`${BTN} bg-(--primary) text-(length:--fs-lg) font-bold text-(--primary-fg)`}
          >
            {request.confirmLabel}
          </button>
          <button type="button" onClick={onClose} className={`${BTN} bg-(--card) text-(length:--fs-md)`}>
            {request.keepLabel}
          </button>
        </div>
      </section>
    </div>,
    host.current,
  );
}

export default ConfirmPanel;
